import bikeCc from '@/core/bikeCc';
import {
  formatWithLeadingZero,
  logEvent,
  mapFuel,
  mapRoofType,
  trackGTMEvent,
  transformPacketTypeToId
} from '@/core/helpers';
import { InsuranceCovers, InsuranceTypes, VehicleTypes } from '@/enums/filters';
import { useOfferStore } from "@/stores/offer";
import { usePackagesStore } from '@/stores/packages';
import { useQuoteStore } from '@/stores/quote';
import axios from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useRouter } from 'vue-router';

import {
  connectEIQWebsockets,
  disconnectEIQWebsockets,
  sendEIQDataToWebsockets,
} from '@/api/eiqAPI';
import {
  fetchInitialDataFromLegacy,
  getBuyNowRedirectLink,
  getOffersByEmailToLegacy,
  postDataForDatabaseToLegacy,
  postDataForRepricingToLegacy,
  postDataForValidation,
  postDeductibleDataToLegacy,
  sendCompareDataToLegacy
} from '@/api/legacyAPI';
import { getFeatureById } from '@/api/productStoreAPI';
import ResultsToast from "@/components/ResultsToast.vue";
import { resolveCheckoutDiscountMandatory } from '@/core/checkoutDiscountTooltip';
import { AnalyticsService } from '@/core/services/analyticsService';
import { CheckoutLinkService } from '@/core/services/checkoutLinkService';
import { FormDataService } from '@/core/services/formDataService';
import { QuoteService } from '@/core/services/quoteService';
import { SelectedProductService } from '@/core/services/selectedProductService';
import taxableHPs from '@/core/taxableHPs';
import { useCheckoutStore } from '@/stores/checkout';
import { useFormStore } from '@/stores/form';
import * as Sentry from '@sentry/browser';
import { useToast } from 'vue-toastification';
import { useUserStore } from './user';

const toast = useToast();

export const useAPIStore = defineStore('api', () => {
  // External stores
  const quoteStore = useQuoteStore();
  const offerStore = useOfferStore();
  const packagesStore = usePackagesStore();
  const userStore = useUserStore();

  const router = useRouter();

  const isLoadingLegacy = ref(false);
  const isLoadingEIQ = ref(false);
  const isLoadingProductStore = ref(false);
  const isLoadingPackets = ref(false)
  const isLoading = ref(false);
  const socket = ref(null);
  const firstResultFired = ref(false);
  const checkoutLinkPending = ref(false);
  const checkoutLinkLoading = ref(false);

  const quoteService = QuoteService();
  const formDataService = FormDataService();
  const analyticsService = AnalyticsService();
  const checkoutLinkService = CheckoutLinkService();

  // Message for event Bus pattern
  const messages = ref([]);
  const manufactureDates = ref({})
  const otherModel = { id : 99999999999, label: 99999999999 };
  const otherManufacturer = { id : 99999999999, label: 99999999999 };


  const EUROTAX_SERVER = import.meta.env.VITE_EUROTAX_SERVER;

  const handleSocketError = (error) => {
    abandonCheckoutLink();
    const fid = ref(quoteStore?.fid);
    const pb_id = ref(quoteStore.quote?.trackingData?.preBuyID);
    // Send GTAG event
    trackGTMEvent({
      "event": "results_error",
      "funnel_stage": "results",
      "module_type": quoteStore.category ? quoteStore.category : 'unknown',
      "module_location": "pageview",
      "event_value": "0",
      "interaction_type": "on_load"
    });
  };

  const updateCompare = async packetIds => {
    let packagesToCompare = [];
    for ( let packetId of packetIds ) {
      let packet = await packagesStore.getPackage(packetId)
      packagesToCompare.push(packet.payload)
    }

    const data = {
      _f: quoteStore.fid,
      quoteState: "pricing",
      packet: quoteStore.quote?.context?.classification?.filters?.packetType,
      eiqResponse: packagesToCompare,
    }

    await sendCompareDataToLegacy(data)
  }

  const reset = () => {
    isLoading.value = true;
    isLoadingPackets.value = true;
    isLoadingLegacy.value = true
    isLoadingEIQ.value = true
  }

  // The chosen package can't be restored: stay on the results
  const abandonCheckoutLink = () => {
    checkoutLinkPending.value = false;
    checkoutLinkLoading.value = false;
  }

  // We will have two main function that will be called from the UI
  // 1. fetchInitialData - will fetch the initial data from the legacy API
  // 2. postDataForRebilling - will post the data to the legacy API and then connect to the websockets server and send the eIQ data
  const resultsMounted = async () => {
    const fid = ref(quoteStore.fid);

    checkoutLinkPending.value = !userStore.isPanelIFrame() && checkoutLinkService.shouldContinueToCheckout(fid.value);
    checkoutLinkLoading.value = checkoutLinkPending.value;
    if (checkoutLinkPending.value) {
      checkoutLinkService.markConsumed(fid.value);
    }

    try {
      // Do the initial fetch
      await fetchInitialData();

    } catch (error) {
      toast.error(ResultsToast, { timeout: 5000 });
      Sentry.captureException(error);

      // Send GTAG event
      trackGTMEvent({
        "event": "results_error",
        "funnel_stage": "results",
        "module_type": quoteStore.category ? quoteStore.category : 'unknown',
        "module_location": "pageview",
        "event_value": "1",
        "error_message": error.message.toString(),
        "interaction_type": "on_load"
      });
    }

    // No (complete) chosen offer on the quote (e.g. it was already ordered): nothing to continue to
    if (checkoutLinkPending.value && !offerStore.hasSelectedPacket()) {
      abandonCheckoutLink();
    }

    try {
      // push message to trigger the results component to update
      messages.value.push('resultsMounted');

      // Connect to the websockets server and send eIQ data
      await connectWebsockets();

      // Send the eIQ data to the websockets server
      sendEIQData();
    } catch (error) {
      abandonCheckoutLink();
      toast.error(ResultsToast, { timeout: 5000 });
      Sentry.captureException(error);

      // Send GTAG event
      trackGTMEvent({
        "event": "results_error",
        "funnel_stage": "results",
        "module_type": quoteStore.category ? quoteStore.category : 'unknown',
        "module_location": "pageview",
        "event_value": "2",
        "error_message": error.message.toString(),
        "interaction_type": "on_load"
      });
    }

  };

  const quoteOrFiltersUpdated = async (source) => {
    // in case there is a previous connection, disconnect
    if (isLoadingEIQ.value) {
      await postDataForDatabase()
    }

    packagesStore.setExpectedNumberOfPackagesWhenWSCloses()

    isLoading.value = true;
    isLoadingPackets.value = true;

    // Reset the package store
    packagesStore.reset();
    try {
      // make API call
      await postDataForRebilling();
    } catch (error) {
      trackGTMEvent({
        "event": "results_error",
        "funnel_stage": "results",
        "module_type": quoteStore.category ? quoteStore.category : 'unknown',
        "module_location": "on_results",
        "event_value": "1",
        "error_message": error.message.toString(),
        "interaction_type": source
      });

      toast.error(`Error posting data: ${error.message.toString()}`);
    }

    try {
      // Connect to the websockets server and send eIQ data
      await connectWebsockets();
      // Send the eIQ data to the websockets server
      sendEIQData();
    } catch (error) {
      trackGTMEvent({
        "event": "results_error",
        "funnel_stage": "results",
        "module_type": quoteStore.category ? quoteStore.category : 'unknown',
        "module_location": "on_results",
        "event_value": "2",
        "error_message": error.message.toString(),
        "interaction_type": source
      });
      toast.error(`Error posting data: ${error.message.toString()}`);
    }

    // push message to trigger the results component to update
    messages.value.push('quoteOrFiltersUpdated');
  };

  //#region LEGACY
  const fetchInitialData = async () => {
    const fid = ref(quoteStore.fid);
    isLoadingLegacy.value = true;

    const isVehicleCategory = quoteStore.category === VehicleTypes.Car || quoteStore.category === VehicleTypes.Bike || quoteStore.category === VehicleTypes.Truck || quoteStore.category === VehicleTypes.Rural || quoteStore.category === VehicleTypes.Taxi || quoteStore.category === VehicleTypes.RentalCar;

    let response
    if(
        (isVehicleCategory) &&
        useUserStore().isIm() &&
        !useUserStore().isPanelIFrame() &&
        !useUserStore().isBestPrice() &&
        !useUserStore().isReqAI()
    ) {
      response = await getEIQPayload(quoteStore.selectedFilters.insuranceCovers);
    } else {
      response = await fetchInitialDataFromLegacy(fid.value);
      formDataService.updateVehicle(quoteStore.category, response);
    }

    quoteStore.handleQuote(response, true);
    quoteStore.setInitialQuote(response)
    offerStore.handleOffer(response?.offerData)

    isLoadingLegacy.value = false;
  };


  const postDataForRebilling = async () => {
    const fid = ref(quoteStore.fid);

    isLoadingLegacy.value = true;

    try {
      quoteService.store({ ...quoteStore.selectedFilters, fid: quoteStore.fid })
      formDataService.updateDuration(quoteStore.category, quoteStore.selectedFilters.insuranceDuration)
      formDataService.updatePacket(quoteStore.category, transformPacketTypeToId(quoteStore.selectedFilters.insuranceType))

      const isVehicleCategory = quoteStore.category === VehicleTypes.Car || quoteStore.category === VehicleTypes.Bike || quoteStore.category === VehicleTypes.Truck || quoteStore.category === VehicleTypes.Rural || quoteStore.category === VehicleTypes.Taxi || quoteStore.category === VehicleTypes.RentalCar;

      let response
      if(
          (isVehicleCategory) &&
          useUserStore().isIm() &&
          !useUserStore().isPanelIFrame() &&
          !useUserStore().isBestPrice() &&
          !useUserStore().isReqAI()
      ) {
        response = await getEIQPayload(quoteStore.selectedFilters.insuranceCovers);
      } else {

        await quoteStore.applyLocalFilters()

        response = await postDataForRepricingToLegacy(
            fid.value,
            quoteStore.appliedFilters,
            quoteService.get().customerJourneyId
        );
      }
      quoteStore.handleQuote(response);
    } catch (error) {
      await logEvent("Failed to apply filters:", "error", {errorMessage: error.message.toString(), redisKey: fid.value, formData: await useFormStore().getFormData(quoteStore.category), filters:quoteStore.selectedFilters})
    }

    isLoadingLegacy.value = false;
  };

  const getOffersByEmail = async (email) => {
    const fid = ref(quoteStore.fid);
    try {
      const response = await getOffersByEmailToLegacy(
          fid.value,
          email,
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  const updateLegacyQuote = async () => {
    const fid = ref(quoteStore.fid);
    await postDataForRepricingToLegacy(
        fid.value,
        quoteStore.appliedFilters,
        quoteService.get().customerJourneyId
    );
  }

  const buyNow = async (id) => {
    // Get the package from the store
    if (isLoadingEIQ.value) {
      await postDataForDatabase()
    }

    const packageToBuy =  await packagesStore.getPackage(id);

    // Create the payload
    const payload = createBuyNowPayload(quoteStore.fid, packageToBuy, generateSelectedMultiproduct(packageToBuy))

    quoteService.store({ ...quoteStore.selectedFilters, fid: quoteStore.fid })

    try {
      await SelectedProductService().clearAppliedDiscount()
      await updateLegacyQuote()

      if(packageToBuy.ownDamagesDeductible) await postDeductibleDataToLegacy({fid: quoteStore.fid, ownDamagesDeductible: packageToBuy.ownDamagesDeductible})

      const response = await getBuyNowRedirectLink(
          payload
      );
      return response;
    } catch (error) {
      toast.error(ResultsToast, { timeout: 5000 });

      trackGTMEvent({
        "event": "results_error",
        "funnel_stage": "results",
        "module_type": quoteStore.category ? quoteStore.category : 'unknown',
        "module_location": "on_buy_button",
        "event_value": "0",
        "interaction_type": "on_buy_button"
      });
      throw error;
    }
  }

  const cleanupOffcanvasArtifacts = () => {
    if (typeof document === 'undefined') {
      return;
    }

    document.querySelectorAll('.offcanvas-backdrop').forEach((backdrop) => backdrop.remove());
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
  };

  const checkoutRedirect = async (url) => {
    const selectedVendor = SelectedProductService().getSelectedProductVendor();
    if (
      (quoteStore.category === VehicleTypes.Car || quoteStore.category === VehicleTypes.Bike) &&
      userStore.isIm() &&
      !userStore.isPanelIFrame()
    ) {
      const navigationToCheckout = await router.push({
        name: 'checkout',
      });

      cleanupOffcanvasArtifacts();
      return navigationToCheckout;
    } else {
      window.location.href = `/${url['url']}`;
    }
  }

    const generateSelectedMultiproduct = (packageToBuy) => {
        const quote = quoteService.get()
        const selectedFilters = quote.insuranceCovers ?? [];

        if (quoteStore.quote?.context.classification.filters.packetType === InsuranceTypes.Premium) {
            selectedFilters.push(InsuranceCovers.OwnDamages);
        }

        let products = [];

        packageToBuy.payload.multiproduct.products.forEach((product, index) => {
            let features = [];

            product.features.forEach(((feature, index) => {
                if (!selectedFilters.includes(feature.type.guid)) {
                    return;
                }

                let deductible = "";
                if (feature.type.guid === InsuranceCovers.OwnDamages) {
                    deductible = feature.attributes.deductibles[0].displayValue;
                }

                features.push({
                    type: feature.type.guid,
                    guid: feature.guid,
                    deductible: deductible
                })
            }));

            products.push({
                guid: product.guid,
                features: features,
            });
        });

        return {
            id: packageToBuy.payload.multiproduct._id,
            products:products,
        }
    };


    // FORM API
  const genericSleepTime = 100;
  const bearerToken = ref(null);
  let apiInstance;

  const prepareGetData = (vehicle) => {
    let data = {};
    if (vehicle.manufacturer) {
      data.manufacturer = vehicle.manufacturer.label;
    }
    if (vehicle.model) {
      data.model = vehicle.model.label;
    }
    if (vehicle.modelEdition) {
      data.modelEdition = vehicle.modelEdition.id;
    }
    if (vehicle.manufactureYear) {
      data.startDate = vehicle.manufactureYear.label;
      data.endDate = vehicle.manufactureYear.label;
      if (vehicle.manufactureMonth){
        data.startDate = addSixMonthsToDate(vehicle.manufactureYear.label, vehicle.manufactureMonth.label)
        data.endDate = subtractSixMonthsFromDate(vehicle.manufactureYear.label, vehicle.manufactureMonth.label)
      }
    }
    return data;
  };

  const auth = async () => {
    if (bearerToken.value) return;

    const response = await axios.get(`${EUROTAX_SERVER}/login`);
    bearerToken.value = response.data;

    apiInstance = axios.create({
      baseURL: `${EUROTAX_SERVER}`,
      headers: {
        'Authorization': `Bearer ${bearerToken.value}`,
      }
    });
  }

  const handleUrlPerCategory = (category) => {
    if (category === VehicleTypes.Taxi || category === VehicleTypes.RentalCar) {
      return VehicleTypes.Car;
    }

    if (category === VehicleTypes.Rural) {
      return VehicleTypes.Truck;
    }

    return category;
  };

  const getManufacturers = async (category, vehicle) => {
    await auth();
    const getData = prepareGetData(vehicle);
    const response = await apiInstance.get(`/${handleUrlPerCategory(category)}/manufacturers`, { params: getData });

    if (response.status !== 200) {
      await logEvent("Failed to retrieve Eurotax manufacturers", "error")
    }

    let manufacturers = response.data.manufacturers;

    if (category === VehicleTypes.Car || category === VehicleTypes.Taxi || category === VehicleTypes.RentalCar) {
      manufacturers.push(otherManufacturer)
      return manufacturers.map(obj => {
        return { id: obj.id, label: obj.label };
      });
    }

    const formattedManufacturers = manufacturers.map(obj => {
      return { id: obj, label: obj };
    })
    formattedManufacturers.push(otherManufacturer);

    return formattedManufacturers;
  };

  const getModels = async (category, vehicle) => {
    await auth();
    const getData = prepareGetData(vehicle);

    try {
      const response = await apiInstance.get(`/${handleUrlPerCategory(category)}/models`, { params: getData });

      let models = Array.isArray(response.data.models) ? response.data.models : [];

      if (category === VehicleTypes.Car || category === VehicleTypes.Taxi || category === VehicleTypes.RentalCar) {
        models.push(otherModel);
        return models;
      }

      let formattedModels = models.map(model => ({ id: model, label: model }));
      formattedModels.push(otherModel);

      return formattedModels;
    } catch (error) {
      if (error.response?.data?.error === "can not find any model") {
        return [otherModel];
      }

      throw error;
    }
  };

  const getManufactureYears = async (category, vehicle) => {
    await auth();
    const getData = prepareGetData(vehicle);
    let response;

    if (getData.model !== otherModel.label && getData.manufacturer !== otherManufacturer.label) {
      try {
        response = await apiInstance.get(`/${handleUrlPerCategory(category)}/modelDates`, { params: getData });
      } catch (error) {
        response = {
          ...response,
          data: {
            startDate: '194901',
            endDate: '199712',
            error: ''
          }
        };
      }
    }

    let startDate = response?.data?.startDate ?? '194901';
    const endDate = response?.data?.endDate ?? '199712'

    //Add extra years (from 1990) for models manufactured between 1991 and 1999
    if (parseInt(startDate.substring(0, 4)) >= 1991 && parseInt(startDate.substring(0, 4)) <= 1999) {
      startDate = '199001';
    }

    //Store them to use them in months calculation
    manufactureDates.startDate = startDate
    manufactureDates.endDate = endDate

    // Calculate the years
    const startYear = parseInt(startDate.substring(0, 4));
    const endYear = parseInt(endDate.substring(0, 4));

    const years = [];
    for (let i = startYear; i <= endYear; i++) {
      years.push({ id: i.toString(), label: i.toString() });
    }

    return years.reverse();
  }

  const getBikeManufactureYears = async () => {
    let years = [];
    for (let i = 1949; i <= new Date().getFullYear(); i++) {
      years.push({ id: i.toString(), label: i.toString() });
    }
    return years.reverse()
  }

  const getVehicleManufacturerMonths = async (vehicle) => {
    const startYear = manufactureDates.startDate.substr(0, 4);
    const endYear = manufactureDates.endDate.substr(0, 4);
    const startYearStartMonth = parseInt(manufactureDates.startDate.substr(4));
    const endYearEndMonth = parseInt(manufactureDates.endDate.substr(4));

    let start = 1;
    let end = 12;

    if (vehicle.manufactureYear.id === startYear) {
      start = startYearStartMonth;
    } else if (vehicle.manufactureYear.id === endYear) {
      end = endYearEndMonth;
    } else if (vehicle.manufactureYear.label === new Date().getFullYear().toString()) {
      end = new Date().getMonth() + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => {
      const month = (start + i).toString().padStart(2, '0');
      return { id: month, label: month };
    });
  };

  const getBikeManufacturerMonths = async () => {
    let months = [];
    for (let i = 1; i <= 12; i++) {
      months.push({ id: i.toString().padStart(2, '0'), label: i.toString().padStart(2, '0') });
    }
    return months
  }

  const getBikeEngineTypes = async () => {
    return [
      {id: 0, label: 'Βενζινοκινητήρας'},
      {id: 1, label: 'Ηλεκτροκινητήρας'},
    ]
  }

  const getBikeCc = async () => {
    return bikeCc;
  }

  const findBikeCcLabelById = async (id) => {
    const ccOptions = await getBikeCc();
    const match = ccOptions.find(option => option.id === id);
    return match ? match.label : null;
  };

  const getBikeKw = async () => {
    let kwOptions = []
    for ( let i = 1; i <= 250; i+=1) {
      kwOptions.push({id: i, label: `${i} κιλοβάτ`})
    }
    return kwOptions
  }

  const getModelEditions = async (category, vehicle) => {
    await auth();
    const getData = prepareGetData(vehicle);

    try {
      const response = await apiInstance.get(`/${handleUrlPerCategory(category)}/editions`, { params: getData });

      let usedLabels = new Set();

      return response.data.editions.reduce((acc, edition) => {
        let label = formatEditionLabel(edition);

        // If the label is already used, skip this edition
        if (usedLabels.has(label)) {
          return acc;
        }

        usedLabels.add(label);

        acc.push({
          id: edition.EurotaxId,
          label: label,
          taxableHP: edition.TaxHorsepower
        });

        return acc;
      }, []);

    } catch (error) {
      // Handle the error, e.g., return null or a custom error object
      return [
        {
          id: "99999999999",
          label: 'Άλλη έκδοση',
          meta: null,
        }
      ]
    }
  }

  // This is the only call that doesnt require a call as it is a static list
  const getTaxableHPs = async () => {
    return taxableHPs;
  };

  const getVehicleByLicensePlate = async (licensePlate, category) => {
    try {
      let response = await getEurotaxInfoFromLicensePlate(licensePlate)
      let transformedData = await getVehicleInfo(response.eurotaxCode, response.firstDate.substring(0, 4), response.firstDate.substring(4), category);
      return transformedData;
    } catch (error) {
      if (error.response?.status >= 500){
        await logEvent("Error while retrieving vehicle by license plate", "error", error);
      }

      throw error
    }
  };

  const getBikeByLicensePlate = async (licensePlate) => {
    try {
      let response = await getEurotaxInfoFromLicensePlate(licensePlate)
      return {
        manufacturer: response.manufacturer,
        model: response.model,
        manufactureYear: parseInt(response.firstDate.substring(0, 4)),
        manufactureMonth: response.firstDate.substring(4),
      }
    } catch (error) {
      if (error.response.status >= 500){
        await logEvent("Error while retrieving vehicle by license plate", "error", error);
      }

      throw error
    }
  }

  const getEurotaxInfoFromLicensePlate = async (licensePlate) => {
    if (!licensePlate) {
      return
    }

    await auth();

    const response = await apiInstance.get('/licensePlate', {
      params: { licensePlate: licensePlate }
    });

    // Check if the response contains an error message
    if (response.data.errorMessage && response.data.errorMessage.length > 0) {
      // Handle the error, e.g., return null or a custom error object
      throw { error: response.data.errorMessage };
    }
    return response.data;
  }

  const getVehicleInfo = async (eurotaxID, year, month, category) => {
    // Subsequent call to get extra info from eurotax
    let extraInfo = await getExtraInfoFromEurotax(eurotaxID, category);
    let manufactureYear = parseInt(year)
    let manufactureMonth = formatWithLeadingZero(parseInt(month))
    let { price, minRecommendedValue, maxRecommendedValue} = await getPriceForModel(extraInfo.edition.EurotaxId, manufactureYear)
    let fuel = mapFuel(extraInfo.edition.EngineType)
    let roofType = mapRoofType(extraInfo.edition.RoofType)

    return {
      manufacturer: extraInfo.edition.Manufacturer,
      manufacturerId: extraInfo.edition.EurotaxManufacturerId || null,
      model: extraInfo.edition.GeneralModel,
      manufactureYear: manufactureYear,
      modelId: extraInfo.edition.EurotaxModelGroupId || null,
      manufactureMonth: manufactureMonth,
      edition: extraInfo.edition.Edition,
      editionId: extraInfo.edition.EurotaxId,
      taxableHP:  extraInfo.edition.TaxHorsepower,
      extraEdition: formatEditionLabel(extraInfo.edition),
      value: price === 0 ? '' : price,
      hasAlarm:	true,
      isHeavy: false,
      hasFridge: false,
      minRecommendedValue: minRecommendedValue,
      maxRecommendedValue: maxRecommendedValue,
      vehicleModelEditionId: extraInfo.edition.GeneralModel,
      vehicleModelText: extraInfo.edition.Edition,
      vehicleModelEdition: extraInfo.edition.EurotaxId,
      vehicleModelExtraEditionText: extraInfo.edition.ExtraEdition,
      combinedHorsepower: parseInt(extraInfo.edition.CombinedHorsepower),
      kw: parseInt(extraInfo.edition.CombinedKw),
      seats: parseInt(extraInfo.edition.SeatNumber),
      cc: parseInt(extraInfo.edition.EngineCapacity),
      emissions: parseInt(extraInfo.edition.Emissions),
      acceleration: parseFloat(extraInfo.edition.Acceleration),
      roofType: roofType,
      fuel: fuel
    };
  }

  const getExtraInfoFromEurotax = async (eurotaxID, category) => {
    await auth();

    const response = await apiInstance.get(`/${handleUrlPerCategory(category)}/edition`, {
      params: {
        editionId: eurotaxID,
      }
    });

    return response.data;

  };

  const formatEditionLabel = (edition) => {
    // Start with the base label
    let label = edition.Edition;

    // Add the extra edition if it exists
    if (edition.ExtraEdition) {
      label += ` ${edition.ExtraEdition}`;
    }

    // Add doors if they exist
    if (edition.DoorNumber) {
      label += ` - ${edition.DoorNumber}θυρο`;
    } else if(edition.SeatNumber){
      label += ` - ${edition.SeatNumber}θυρο`;
    }

    // Add engine type if it exists
    if (edition.EngineType) {
      label += ` (${edition.EngineType})`;
    }

    return label;
  };

  const addSixMonthsToDate = (year, month) => {
    let date = new Date(year, month);
    date.setMonth(date.getMonth() + 6)

    return  date.getFullYear().toString() + date.getMonth().toString().padStart(2, '0')
  };

  const subtractSixMonthsFromDate = (year, month) => {
    let date = new Date(year, month);
    date.setMonth(date.getMonth() - 6)

    return  date.getFullYear().toString() + date.getMonth().toString().padStart(2, '0')
  };

  const getPriceForModel = async (editionID, firstYear) => {
    try {
      await auth();
      const response = await apiInstance.get('/car/price', {
        params: {
          editionId: editionID,
          firstYear: firstYear,
        }
      });
      return {
        price: parseInt(response.data.price),
        minRecommendedValue: parseInt(response.data.minRecommendedValue),
        maxRecommendedValue: parseInt(response.data.maxRecommendedValue),
      }
    } catch (error){
        let errPrice
        let errMinRecommendedValue
        let errMaxRecommendedValue

        if (new Date().getFullYear() - firstYear >= 16) {
            errPrice = 3000
            errMinRecommendedValue = 2700
            errMaxRecommendedValue = 3300
        }

        if (new Date().getFullYear() - firstYear <= 15) {
            errPrice = 9000
            errMinRecommendedValue = 3000
            errMaxRecommendedValue = 15000
        }

      return {
        price: parseInt(errPrice),
        minRecommendedValue: parseInt(errMinRecommendedValue),
        maxRecommendedValue: parseInt(errMaxRecommendedValue),
      }
    }
  }

  const getBikePriceForModel = async (year, isElectric, cc, kw) => {
    try {
      await auth();

      const endpoint = isElectric ? '/bike/electric/price' : '/bike/price';
      const params = isElectric ? { year, kw } : { year, cc };

      const response = await apiInstance.get(endpoint, { params });

      const { price, minRecommendedValue, maxRecommendedValue } = response.data;

      return {
        price: Number(price),
        minRecommendedValue: Number(minRecommendedValue),
        maxRecommendedValue: Number(maxRecommendedValue),
      };
    } catch (error) {
      return {
        price: null,
        minRecommendedValue: null,
        maxRecommendedValue: null,
      };
    }
  };

  const postFormData = async (formData, validationPath) => {
    return await postDataForValidation(formData, quoteStore.fid, validationPath)
  };

  // #endregion
  const createBuyNowPayload = (fid, packet, selectedMultiproduct) => {
    return {
      _f: fid,
      quoteState: "pricing",
      company_id: packet.company.id,
      packet_id: packet.packageId,
      isEasyBuy: packet.easyBuy,
      offerDetails: {
          customerSelection: {
              multiproduct: selectedMultiproduct,
          }
      },
      trackingData: {
          customerJourneyId: quoteService.get().customerJourneyId
      },
      eiqResponse: packet.payload,
    }
  }

  const buyNowCompare = async packet => {
    let payload = createBuyNowPayload(quoteStore.fid, packet, generateSelectedMultiproduct(packet))
    try {
      if(packet.ownDamagesDeductible) await postDeductibleDataToLegacy({fid: quoteStore.fid, ownDamagesDeductible: packet.ownDamagesDeductible})

      const isVehicleCategory = quoteStore.category === VehicleTypes.Car || quoteStore.category === VehicleTypes.Bike || quoteStore.category === VehicleTypes.Truck || quoteStore.category === VehicleTypes.Rural || quoteStore.category === VehicleTypes.Taxi || quoteStore.category === VehicleTypes.RentalCar;

      if (
          (isVehicleCategory) &&
          useUserStore().isIm() &&
          !useUserStore().isPanelIFrame() &&
          !useUserStore().isBestPrice() &&
          !useUserStore().isReqAI()
      ) {
        await updateLegacyQuote()
      }

      await SelectedProductService().clearAppliedDiscount()

      const response = await getBuyNowRedirectLink(
          payload
      );
      return response;
    } catch (error) {
      toast.error(ResultsToast, { timeout: 5000 });

      trackGTMEvent({
        "event": "results_error",
        "funnel_stage": "results",
        "module_type": quoteStore.category ? quoteStore.category : 'unknown',
        "module_location": "on_buy_button",
        "event_value": "0",
        "interaction_type": "on_buy_button"
      });
      throw error;
    }

  }

  //#region EIQ
  const connectWebsockets = async () => {
    await connectEIQWebsockets(socket, handleSocketError, handleOnSocketClose);
  };

  const sendEIQData = () => {
    isLoadingEIQ.value = true;
    firstResultFired.value = false;
    sendEIQDataToWebsockets(socket, quoteStore.quote, handleIncomingMessages);
  };

  const disconnectWebsockets = () => {
    disconnectEIQWebsockets(socket);
    if (packagesStore.packages.length === 0){
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({'event': 'no_results_error'});
    }
  };

  const handleIncomingMessages = (message) => {
    if (message.type === "QUOTED_PRODUCT_RESPONSE" && !isLoadingPackets.value) {
      if (packagesStore.addPackage(message.payload)) {
        if (!firstResultFired.value) {
          trackGTMEvent({
            event: 'results_appear',
            pb_id: quoteStore.quote?.trackingData?.preBuyID,
          });
          firstResultFired.value = true;
        }
        continueToCheckoutIfChosenPackage(packagesStore.packages[packagesStore.packages.length - 1]);
        if (packagesStore.allPackagesReceived()) {
          allPackagesReceived();
        }
      }
    } else if (message.type === "NUMBER_OF_PRODUCTS") {
      isLoadingPackets.value = false
      packagesStore.setExpectedNumberOfPackages(message.payload.numberOfProducts);
    } else if ( message.type === "CARD_COVER_PRICES" ) {
      packagesStore.setAutonomousCovers(message)
    }
  };

  const handleOnSocketClose = async _ => {
    packagesStore.setExpectedNumberOfPackagesWhenWSCloses()

    if (isLoadingEIQ.value) {
      await postDataForDatabase()
    }

    allPackagesReceived()

  }

  // Checkout link: buy the package the user had chosen as soon as it is streamed
  const continueToCheckoutIfChosenPackage = async (streamedPackage) => {
    if (!checkoutLinkPending.value || !offerStore.isPacketSelectedByUser(streamedPackage)) {
      return;
    }
    checkoutLinkPending.value = false;

    // The buy button asks for a coupon first for these packages
    if (resolveCheckoutDiscountMandatory(streamedPackage)) {
      abandonCheckoutLink();
      return;
    }

    let redirectURL;
    try {
      redirectURL = await buyNow(streamedPackage.id);
    } catch {
      abandonCheckoutLink();
      return;
    }

    try {
      packagesStore.storeSelectedProduct(streamedPackage, redirectURL.offerCode, redirectURL.dc);

      // A link must not prefill personal details saved on this device
      useCheckoutStore().resetCheckoutData();

      await checkoutRedirect(redirectURL);
    } catch {
      abandonCheckoutLink();
      toast.error(ResultsToast, { timeout: 5000 });
    }
  };

  const allPackagesReceived = () => {
    // The package of the checkout link was not among the received ones
    if (checkoutLinkPending.value) {
      abandonCheckoutLink();
    }

    // We received all the packages, stop listening and disconnect from the websockets server
    socket.value.onmessage = null;
    disconnectWebsockets();
    isLoadingEIQ.value = false;
    isLoading.value = false;
    isLoadingPackets.value = false
  };

  // #endregion
  const pushMessage = (newMessage) => {
    messages.value.push(newMessage);
  };

  const postDataForDatabase = async () => {
    const http_status_code_conflict = 409;
    try {
      packagesStore.filteredOutPackages = []
      return await postDataForDatabaseToLegacy({quoteStore, packagesStore});
    } catch (error) {
      if (error.status === http_status_code_conflict) {
        toast.error(ResultsToast, { timeout: 5000 });
      }
      return error
    }
  };

  const getCover = async (cover_id) => {
    isLoadingProductStore.value = true;

    let res
    let response
    try {
      response = await getFeatureById(cover_id);
      res = response.results;
      packagesStore.addCover(cover_id,res);
    } catch (error){
      await logEvent(
          "Error while adding covers",
          "error",
          {errorMessage: error.message.toString(), coverId: cover_id, response: response}
      );
    }

    isLoadingProductStore.value = false;
    return res;
  };

  const getEIQPayload = async (selectedFilters) => {
    let data = await useFormStore().getFormData(quoteStore.category)
    quoteService.store({ vehicleType: quoteStore.category })
    
    let trackingData = await analyticsService.fetchTrackingData(quoteStore.fid)
    if( quoteStore.quote?.trackingData) {
      quoteStore.quote.trackingData = trackingData
    }

    return quoteService.createQuoteRequest(data, selectedFilters, trackingData);
  }

  return {
    pushMessage,
    resultsMounted,
    quoteOrFiltersUpdated,
    updateCompare,
    getOffersByEmail,
    buyNow,
    checkoutRedirect,
    postDataForRebilling,
    buyNowCompare,
    getManufacturers,
    getModels,
    getBikeManufactureYears,
    getManufactureYears,
    getBikeManufacturerMonths,
    getVehicleManufacturerMonths,
    getBikeEngineTypes,
    getBikeCc,
    getBikeKw,
    findBikeCcLabelById,
    getModelEditions,
    getTaxableHPs,
    getVehicleByLicensePlate,
    getPriceForModel,
    getBikePriceForModel,
    getExtraInfoFromEurotax,
    getVehicleInfo,
    getBikeByLicensePlate,
    postFormData,
    isLoadingLegacy,
    isLoadingEIQ,
    isLoading,
    checkoutLinkLoading,
    isLoadingPackets,
    isLoadingProductStore,
    messages,
    postDataForDatabase,
    getCover,
    reset,
    createBuyNowPayload,
    generateSelectedMultiproduct
  };
});
